import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as S from "./ModalChangeProgramStatus.style";
import * as P from "../../../../../utils/pipes";
import { Actions } from "../../../../../utils/actionsIds";

// graphQL
import { useQuery, useMutation } from "@apollo/client";

// hooks
import ZaiaReaction from "../../../../zaiaComponents/zaiaReaction/ZaiaReaction";
import ZaiaModalResponseContent from "../../../../zaiaComponents/zaiaModalResponseContent";
import ZaiaSelect from "../../../../zaiaComponents/zaiaSelect";
import useRecordNavigation from "../../../../../hooks/useRecordNavigation";
import ZaiaInput from "../../../../zaiaComponents/zaiaInput";
import { QUERY_GET_PROCESS_STAGES_BY_PROCESS_IDS } from "../../../../../graphql/queries/UserToProcess";
import { MUTATION_UPDATE_USER_TO_PROCESS_FOLLOW_UP } from "../../../../../graphql/mutations/UserToProcess";
import EndStatusChange from "./components/endStatusChange";
import ListStages from "./components/listStages";
import FlorenzButton from "../../../../florenzComponents/florenzButton";
import ListPrograms from "./components/listPrograms";
import ListGroupStages from "./components/listGroupStages";
import FormSteps from "./components/formSteps";
import { QUERY_GET_ALL_PROCESS_GROUP_STAGES, QUERY_GET_ALL_PROCESS_GROUP_STAGES_BY_PROCESS_ID } from "../../../../../graphql/queries/ProcessStage";

const zaiaImgLoading = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/singularity-health-loader.svg`;

function ModalChangeProgramStatus({ 
  setChangeProgramStatus,
  patient
}) {

  const {t, i18n: { language }} = useTranslation("global");
  const [updateUserToProcess, {loading: updateUserToProcessLoading}] = useMutation(MUTATION_UPDATE_USER_TO_PROCESS_FOLLOW_UP);
  const [processSelected, setProcessSelected] = useState(patient?.userToProcesses[0]?.process?.id);
  const [formSuccess, setFormSuccess] = useState(null);
  const [successText, setSuccessText] = useState("");
  const { recordAction } = useRecordNavigation();
  const [statusDescription, setStatusDescription] = useState("");
  const [statusDate, setStatusDate] = useState(null);
  const [changeStatusSteps, setChangeStatusSteps] = useState([]);

  const [statusSelectionView, setStatusSelectionView] = useState(0);

  const { data: processStagesData } = useQuery(QUERY_GET_PROCESS_STAGES_BY_PROCESS_IDS, {
    variables: { 
      processIds: [processSelected]
    },
  })

  const [currentStatus, setCurrentStatus] = useState();
  const [newCurrentStatus, setNewCurrentStatus] = useState();
  const [counterStage, setCounterStage] = useState(null);
  const [newCounterStage, setNewCounterStage] = useState(null);

  const { 
    data: processGroupStages,
  } = useQuery(QUERY_GET_ALL_PROCESS_GROUP_STAGES, 
    {
      skip: processSelected,
    }
  );   

  const { data: processGroupStagesByProcessId } = useQuery(QUERY_GET_ALL_PROCESS_GROUP_STAGES_BY_PROCESS_ID, {
    variables: {
      processId: processSelected,
    },
    skip: !processSelected
  });

  useEffect(() => {
    const userToProcessSelected = patient?.userToProcesses.find((userToProcess) => userToProcess.process.id === processSelected);
    const status = userToProcessSelected?.processStage?.id;
    
    const count = userToProcessSelected.stageExtraInfo ? Number(userToProcessSelected.stageExtraInfo) : 1;
    setCounterStage(count)
    setNewCounterStage(count)
    
    setCurrentStatus(status);
    setNewCurrentStatus(status);
  }, [processSelected, patient]);  

  useEffect(() => {

    const processGroupStagesData = processSelected ? processGroupStagesByProcessId : processGroupStages;
    const newStatusData = processStagesData?.process[0]?.processStages.find((item) => item.id === newCurrentStatus)
    
    setChangeStatusSteps(
      processGroupStagesData?.groupStage.length > 0
      ? 
      [
        {
          id: 1,
          title: t("changeOfStatus.changeGroup"),
          cmp: ListGroupStages
        },
        {
          id: 2,
          title: t("changeOfStatus.changeStatus"),
          cmp: ListStages,
          validation: ((newCurrentStatus === currentStatus) && (newStatusData?.incremental && counterStage === newCounterStage))
        },
        {
          id: 3,
          title: t("changeOfStatus.details"),
          cmp: EndStatusChange,
          disabled: ((newCurrentStatus === currentStatus) && (newStatusData?.incremental && counterStage === newCounterStage))
        }
      ] 
      : [
        {
          id: 1,
          title: t("changeOfStatus.changeStatus"),
          cmp: ListStages,
          validation: newCurrentStatus === currentStatus
        },
        {
          id: 2,
          title: t("changeOfStatus.details"),
          cmp: EndStatusChange,
          disabled: ((newCurrentStatus === currentStatus) && (newStatusData?.incremental && counterStage === newCounterStage))
        }
      ]
    );          
  }, [
    processSelected, 
    processGroupStages, 
    statusSelectionView, 
    newCurrentStatus, 
    currentStatus,  
    counterStage, 
    newCounterStage
  ]);

  const frequency = (stage) => {    
    if(stage) {
      if(stage.frequencyType === "days") {
        return (
          `${t("globally.each")}
          ${stage.frequency > 1 ? stage.frequency : ""} 
          ${stage.frequency > 1 ? t("globally.days") : t("globally.day")}`
        ) 
      } else if(stage.frequencyType === "months") {
        return (
          `${t("globally.each")} 
          ${stage.frequency > 1 ? stage.frequency : ""} 
          ${stage.frequency > 1 ? t("globally.months") : t("globally.month")}`
        ) 
      } else {
        return (`${t("globally.each")} ${stage.frequency}`)
      }
    }
  } 
  
  const newCurrenStatusData = useMemo(() => {
    return processStagesData?.process[0]?.processStages.find((item) => item.id === newCurrentStatus);
  }, [newCurrentStatus, processStagesData])   
   
  const handleSubmit = async () => {    
    try {
      await updateUserToProcess({
        variables: {
          updates: {
            stageId: newCurrentStatus,
            stageExtraInfo: newCurrenStatusData?.incremental && newCounterStage ? String(newCounterStage) : null,
            descript: statusDescription,
            statusChangedDate: new Date(statusDate)
          },
          userToProcessId: patient.userToProcesses.find((item) => item.process?.id === processSelected).id
        },
      });
      recordAction(Actions.STATUS_MANAGEMENT.SAVE_STATUS, null, processSelected);
      setCurrentStatus(newCurrentStatus);
      setStatusSelectionView(0);
      setStatusDescription("")
      setStatusDate(null)
      setFormSuccess(true);
      setSuccessText(t("changeOfStatus.successChangingStatus"));
    } catch (error) {
      setFormSuccess(false);
      setSuccessText(t("changeOfStatus.errorChangingStatus"));
      console.log(error);
    }
  }

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.keyCode === 27) {
        setChangeProgramStatus(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    // Don't forget to clean up
    return function cleanup() {
      document.removeEventListener('keydown', handleKeyDown);
    }
  }, []);

  if(
    updateUserToProcessLoading
  ) 
    return(
      <S.ModalChangeProgramStatus>
        <div className="modal-background">
        </div>
        <div className="modal-container modal-container--response">
          <ZaiaReaction
            widthimg={"150px"}
            zaiaImg={zaiaImgLoading}
            padding={"200px 0px"}
          />
        </div>
      </S.ModalChangeProgramStatus>
    )

  if(formSuccess)
    return(
      <S.ModalChangeProgramStatus>
        <div className="modal-background" onClick={() => setChangeProgramStatus(false)}>
        </div>
        <div className="modal-container modal-container--response">
          <ZaiaModalResponseContent
            button1={() => setFormSuccess(null)}
            button1Text={t("globally.done")}
            formSuccess={formSuccess}
            title={successText}
          />
        </div>
      </S.ModalChangeProgramStatus>
    )

  if(formSuccess === false)
    return(
      <S.ModalChangeProgramStatus>
        <div className="modal-background" onClick={() => setChangeProgramStatus(false)}>
        </div>
        <div className="modal-container modal-container--response">
          <ZaiaModalResponseContent
            button1={() => setFormSuccess(null)}
            formSuccess={formSuccess}
            button1Text={t("globally.accept")}
            title={t("globally.errorForm")}
            subtitle={successText}
          />
        </div>
      </S.ModalChangeProgramStatus>
    )    

  return (
    <S.ModalChangeProgramStatus
      programSelected={statusSelectionView > 0}
    >
      <div className="modal-background" onClick={() => setChangeProgramStatus(false)}>
      </div>
      <div className="modal-container">
        <div className="header">
          <button className="close"
            onClick={() => setChangeProgramStatus(false)}
          >
            <i className="close__icon icon zaia-icono-cerrar"></i>
          </button>
          <div className="header__text">
            <div className="title">{t("changeOfStatus.title")}</div>
            <div className="description">
              {t("changeOfStatus.aboutProgramStatusSub")}
            </div>
          </div>
        </div>
        <div className="content">
          <div className="content__patient-card">
            {
              statusSelectionView !== 0 &&
              <div className="program-info">
                <div className="program-info__label">{t("globally.program")}</div>
                <div className="program-info__program-selected">
                  {P.dataTranslation(patient?.userToProcesses.find((item) => item.process?.id === processSelected)?.process?.processName, language)}
                </div>
              </div>
            }
            <div className="patient-image">
              <div className="patient-image__border"></div>
              <img
                className="patient-image__img"
                src={patient.picture}
                alt={patient.name}
              />
              <img
                className="patient-image__img-shadow"
                src={patient.picture}
                alt={patient.name}
              />
            </div>
            <div className="patient-principal-info">
              <div className="patient-name">
                {patient.name} {patient.lastname}
              </div>
              <div className="patient-info">
                <div className="patient-info__years">
                  {P.getAge(patient?.birthdate)} {t("aiNavigator.outcomes.yearsOld")}
                </div>
                <div className="patient-info__separator"></div>
                <div className="patient-info__id-number">
                  {patient.enDocumentType?.comment.code} {patient.documentNumber}
                </div>
              </div>
            </div>
          </div>
          {
            statusSelectionView === 0 
            ? <ListPrograms
              processList={patient?.userToProcesses}
              frequency={frequency}
              processSelected={processSelected}
              setProcessSelected={setProcessSelected}
              setStatusSelectionView={setStatusSelectionView}
            />
            : <FormSteps
              processStagesData={processStagesData}
              currentStatus={currentStatus}
              newCurrenStatusData={newCurrenStatusData}
              statusDescription={statusDescription}
              setStatusDescription={setStatusDescription}
              statusDate={statusDate}
              setStatusDate={setStatusDate}
              frequency={frequency}
              patient={patient}
              processSelected={processSelected}
              statusSelectionView={statusSelectionView}
              setStatusSelectionView={setStatusSelectionView}
              newCurrentStatus={newCurrentStatus}
              setNewCurrentStatus={setNewCurrentStatus}
              counterStage={counterStage}
              setCounterStage={setCounterStage}
              newCounterStage={newCounterStage}
              setNewCounterStage={setNewCounterStage}
              changeStatusSteps={changeStatusSteps}
              processGroupStages={processSelected ? processGroupStagesByProcessId : processGroupStages}
            />
          }
        </div>
        {
          statusSelectionView !== 0 &&
          <div className="footer">
            <div className="footer__description">
              {
                statusSelectionView > 0 && statusSelectionView !== 3 &&
                <div className="footer__description-text">
                  {t("changeOfStatus.fillAllFields")} <b
                    className="error"
                  >*</b>
                </div>
              }
            </div>
            <div className="footer__buttons">
              {
                statusSelectionView > 0 &&
                <FlorenzButton
                  typeStyle={"secondary"}
                  size="s"
                  text={t("globally.cancel")}
                  onClick={() => {
                    setStatusSelectionView(0)
                    recordAction(Actions.STATUS_MANAGEMENT.CANCEL_STATUS, null, processSelected);
                  }}
                />
              }
              <div className="button-separator"></div>
              {
                statusSelectionView > 0 &&
                <div className="button-container">
                  <FlorenzButton
                    typeStyle={"secondary"}
                    size="s"
                    text={t("globally.back")}
                    onClick={() => setStatusSelectionView(statusSelectionView - 1)}
                    disabled={statusSelectionView <= 1 }
                    icon={
                      statusSelectionView <= 1 && "zaia-i-lock-chat"
                    }
                  />
                </div>
              }
              {
                statusSelectionView > 0 && statusSelectionView !== changeStatusSteps.length &&
                <div className="button-container">
                  <FlorenzButton
                    typeStyle={"primary"}
                    size="s"
                    text={t("globally.next")}
                    onClick={() => setStatusSelectionView(statusSelectionView + 1)}
                    disabled={changeStatusSteps[statusSelectionView - 1].validation}
                    icon={
                      changeStatusSteps[statusSelectionView - 1].validation
                      ? "zaia-i-lock-chat"
                      : "zaia-i-next"
                    }
                  />
                </div>
              }
              {
                statusSelectionView === changeStatusSteps.length &&
                <FlorenzButton
                  typeStyle={"success"}
                  size="s"
                  text={t("changeOfStatus.saveChanges")}
                  onClick={() => (statusDescription && statusDate) && handleSubmit()}
                  disabled={!statusDescription || !statusDate}
                  icon={
                    (!statusDescription || !statusDate)
                    && "zaia-i-lock-chat"
                  }
                />
              }
            </div>
          </div>
        }
      </div>
    </S.ModalChangeProgramStatus>
  );
}

export default ModalChangeProgramStatus;
